import { useAuthContext } from 'context/AuthContext'

export type FieldType =
  | 'short_text'
  | 'long_text'
  | 'radio'
  | 'checkbox'
  | 'accept'
  | 'upload'
  | 'date'
export type ContextType =
  | 'internal_customer'
  | 'customer'
  | 'product_link'
  | 'contract_model'

interface FieldTypePermission {
  hasAccess: boolean
  plan: string
  isDisabled: boolean
}

interface ContextPermission {
  hasAccess: boolean
  plan: string
  isDisabled: boolean
}

interface UseCustomFieldPermissionsReturn {
  fieldTypes: Record<FieldType, FieldTypePermission>
  contexts: Record<ContextType, ContextPermission>
  canCreateField: (fieldType: FieldType) => boolean
  canUseContext: (contextType: ContextType) => boolean
  getFieldTypePermission: (fieldType: FieldType) => FieldTypePermission
  getContextPermission: (contextType: ContextType) => ContextPermission
  isFieldTypeDisabled: (fieldType: FieldType) => boolean
  isContextDisabled: (contextType: ContextType) => boolean
}

export const useCustomFieldPermissions =
  (): UseCustomFieldPermissionsReturn => {
    const { capabilities } = useAuthContext()

    // `capabilities` nasce null: o claim saiu do JWT (SC-10878) e o AuthProvider
    // busca /v1/merchant_capabilities na montagem. Enquanto a resposta não chega
    // não dá para afirmar que o merchant NÃO contratou o recurso, e tratar isso
    // como "sem acesso" desabilitava todos os tipos de campo e o modelo de
    // contrato — sem nem o selo de upgrade, que some quando não há `plan`, então
    // o campo ficava cinza e inclicável sem explicação nenhuma (SC-10922).
    //
    // Mesmo tratamento que o `useModuleAccess` já faz. `== null` cobre null e
    // undefined.
    const isLoading = capabilities == null

    // Mapear permissões de tipos de campo
    const fieldTypes: Record<FieldType, FieldTypePermission> = {
      short_text: {
        hasAccess:
          capabilities?.['custom_field.field_types.short_text']?.has_access ||
          false,
        plan:
          capabilities?.['custom_field.field_types.short_text']?.plan || 'free',
        isDisabled:
          !isLoading &&
          !capabilities?.['custom_field.field_types.short_text']?.has_access
      },
      long_text: {
        hasAccess:
          capabilities?.['custom_field.field_types.long_text']?.has_access ||
          false,
        plan:
          capabilities?.['custom_field.field_types.long_text']?.plan || 'start',
        isDisabled:
          !isLoading &&
          !capabilities?.['custom_field.field_types.long_text']?.has_access
      },
      radio: {
        hasAccess:
          capabilities?.['custom_field.field_types.radio']?.has_access || false,
        plan: capabilities?.['custom_field.field_types.radio']?.plan || 'start',
        isDisabled:
          !isLoading &&
          !capabilities?.['custom_field.field_types.radio']?.has_access
      },
      checkbox: {
        hasAccess:
          capabilities?.['custom_field.field_types.checkbox']?.has_access ||
          false,
        plan:
          capabilities?.['custom_field.field_types.checkbox']?.plan || 'start',
        isDisabled:
          !isLoading &&
          !capabilities?.['custom_field.field_types.checkbox']?.has_access
      },
      accept: {
        hasAccess:
          capabilities?.['custom_field.field_types.accept']?.has_access ||
          false,
        plan: capabilities?.['custom_field.field_types.accept']?.plan || 'grow',
        isDisabled:
          !isLoading &&
          !capabilities?.['custom_field.field_types.accept']?.has_access
      },
      upload: {
        hasAccess:
          capabilities?.['custom_field.field_types.upload']?.has_access ||
          false,
        plan: capabilities?.['custom_field.field_types.upload']?.plan || 'grow',
        isDisabled:
          !isLoading &&
          !capabilities?.['custom_field.field_types.upload']?.has_access
      },
      date: {
        hasAccess:
          capabilities?.['custom_field.field_types.date']?.has_access || false,
        plan: capabilities?.['custom_field.field_types.date']?.plan || 'start',
        isDisabled:
          !isLoading &&
          !capabilities?.['custom_field.field_types.date']?.has_access
      }
    }

    // Mapear permissões de contextos
    const contexts: Record<ContextType, ContextPermission> = {
      internal_customer: {
        hasAccess:
          capabilities?.['custom_field.contexts.internal_customer']
            ?.has_access || false,
        plan:
          capabilities?.['custom_field.contexts.internal_customer']?.plan ||
          'free',
        isDisabled:
          !isLoading &&
          !capabilities?.['custom_field.contexts.internal_customer']?.has_access
      },
      customer: {
        hasAccess:
          capabilities?.['custom_field.contexts.customer']?.has_access || false,
        plan: capabilities?.['custom_field.contexts.customer']?.plan || 'start',
        isDisabled:
          !isLoading &&
          !capabilities?.['custom_field.contexts.customer']?.has_access
      },
      product_link: {
        hasAccess:
          capabilities?.['custom_field.contexts.product_link']?.has_access ||
          false,
        plan:
          capabilities?.['custom_field.contexts.product_link']?.plan || 'start',
        isDisabled:
          !isLoading &&
          !capabilities?.['custom_field.contexts.product_link']?.has_access
      },
      contract_model: {
        hasAccess:
          capabilities?.['custom_field.contexts.contract_model']?.has_access ||
          false,
        plan:
          capabilities?.['custom_field.contexts.contract_model']?.plan ||
          'basic',
        isDisabled:
          !isLoading &&
          !capabilities?.['custom_field.contexts.contract_model']?.has_access
      }
    }

    // Funções auxiliares
    const canCreateField = (fieldType: FieldType): boolean => {
      return fieldTypes[fieldType]?.hasAccess || false
    }

    const canUseContext = (contextType: ContextType): boolean => {
      return contexts[contextType]?.hasAccess || false
    }

    const getFieldTypePermission = (
      fieldType: FieldType
    ): FieldTypePermission => {
      return (
        fieldTypes[fieldType] || {
          hasAccess: false,
          plan: 'start',
          isDisabled: true
        }
      )
    }

    const getContextPermission = (
      contextType: ContextType
    ): ContextPermission => {
      return (
        contexts[contextType] || {
          hasAccess: false,
          plan: 'start',
          isDisabled: true
        }
      )
    }

    const isFieldTypeDisabled = (fieldType: FieldType): boolean => {
      return fieldTypes[fieldType]?.isDisabled || false
    }

    const isContextDisabled = (contextType: ContextType): boolean => {
      return contexts[contextType]?.isDisabled || false
    }

    return {
      fieldTypes,
      contexts,
      canCreateField,
      canUseContext,
      getFieldTypePermission,
      getContextPermission,
      isFieldTypeDisabled,
      isContextDisabled
    }
  }
